<template>
    <div class="integration-logs">
        <!-- Header com título e informações dos filtros aplicados -->
        <div class="dashboard-header mb-4">
            <div class="row align-items-center">
                <div class="col-md-6">
                    <h5 class="dashboard-title text-white">
                        <i class="fas fa-chart-line mr-2"></i>
                        Dashboard de Integrações
                    </h5>
                </div>
                <div class="col-md-6 text-right">
                    <div
                        class="filters-applied"
                        v-if="hasActiveFilters()"
                    >
                        <small class="text-white">
                            <i class="fas fa-filter mr-1"></i>
                            {{ getActiveFiltersCount() }} filtro(s) aplicado(s)
                        </small>
                    </div>
                    <div class="last-update">
                        <small class="text-white">
                            <i class="fas fa-clock mr-1"></i>
                            Última atualização: {{ formatDateTime(new Date()) }}
                        </small>
                    </div>
                </div>
            </div>
        </div>

        <!-- Seção de Filtros Avançados -->
        <div class="filters-section mb-4">
            <div class="card shadow-sm border-0">
                <div class="card-header bg-gradient-primary text-white">
                    <h5 class="card-title mb-0">
                        <i class="fas fa-filter mr-2"></i>
                        Filtros Avançados
                        <button
                            class="btn btn-sm btn-outline-light float-right"
                            @click="toggleFilters"
                        >
                            <i
                                class="fas"
                                :class="showFilters ? 'fa-eye-slash' : 'fa-eye'"
                            ></i>
                            {{ showFilters ? 'Ocultar' : 'Mostrar' }} Filtros
                        </button>
                    </h5>
                </div>
                <div
                    class="card-body"
                    v-show="showFilters"
                >
                    <div class="row">
                        <!-- Filtros existentes mantidos -->
                        <div class="col-md-3 mb-3">
                            <label class="form-label">Status da Integração</label>
                            <select
                                v-model="customFilters.status"
                                class="form-control"
                                @change="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            >
                                <option value="">Todos os Status</option>
                                <option value="success">Sucesso</option>
                                <option value="error">Erro</option>
                                <option value="timeout">Timeout</option>
                                <option value="processing">Processando</option>
                            </select>
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">Método de Integração</label>
                            <select
                                v-model="customFilters.method"
                                class="form-control"
                                @change="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            >
                                <option value="">Todos os Métodos</option>
                                <option value="authenticate">Autenticação</option>
                                <option value="user">Dados do Usuário</option>
                                <option value="setReserve">Criar Reserva</option>
                                <option value="updateReserve">Atualizar Reserva</option>
                            </select>
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">Tipo de Erro</label>
                            <select
                                v-model="customFilters.error_type"
                                class="form-control"
                                @change="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            >
                                <option value="">Todos os Tipos</option>
                                <option value="connect_error">Erro de Conexão</option>
                                <option value="server_error">Erro do Servidor</option>
                                <option value="client_error">Erro da Requisição</option>
                                <option value="internal_error">Erro Interno</option>
                                <option value="api_error">Erro de API</option>
                            </select>
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">ID da Reserva</label>
                            <input
                                v-model="customFilters.entity_id"
                                type="text"
                                class="form-control"
                                placeholder="Digite o ID..."
                                @input="debounceFilter"
                                :disabled="filtersLoading || refreshing"
                            />
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">Data Inicial</label>
                            <input
                                v-model="customFilters.date_from"
                                type="date"
                                class="form-control"
                                @change="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            />
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">Data Final</label>
                            <input
                                v-model="customFilters.date_to"
                                type="date"
                                class="form-control"
                                @change="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            />
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">Código HTTP</label>
                            <select
                                v-model="customFilters.http_status"
                                class="form-control"
                                @change="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            >
                                <option value="">Todos os Códigos</option>
                                <option value="200">200 - Sucesso</option>
                                <option value="400">400 - Bad Request</option>
                                <option value="401">401 - Unauthorized</option>
                                <option value="404">404 - Not Found</option>
                                <option value="500">500 - Server Error</option>
                                <option value="503">503 - Service Unavailable</option>
                            </select>
                        </div>

                        <div class="col-md-3 mb-3">
                            <label class="form-label">Período Rápido</label>
                            <select
                                v-model="customFilters.quick_period"
                                class="form-control"
                                @change="applyQuickPeriod"
                                :disabled="filtersLoading || refreshing"
                            >
                                <option value="">Período Personalizado</option>
                                <option value="today">Hoje</option>
                                <option value="yesterday">Ontem</option>
                                <option value="week">Esta Semana</option>
                                <option value="month">Este Mês</option>
                                <option value="last_week">Semana Passada</option>
                                <option value="last_month">Mês Passado</option>
                            </select>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12">
                            <button
                                class="btn btn-primary mr-2 w-auto"
                                @click="applyFilters"
                                :disabled="filtersLoading || refreshing"
                            >
                                <i
                                    class="fas fa-search mr-2"
                                    v-if="!filtersLoading && !refreshing"
                                ></i>
                                <i
                                    class="fas fa-spinner fa-spin mr-2"
                                    v-if="filtersLoading || refreshing"
                                ></i>
                                {{ (filtersLoading || refreshing) ? 'Aplicando...' : 'Aplicar Filtros' }}
                            </button>
                            <button
                                class="btn btn-secondary mr-2 w-auto"
                                @click="clearFilters"
                                :disabled="filtersLoading || refreshing"
                            >
                                <i class="fas fa-times mr-2"></i>
                                Limpar Filtros
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cards de Estatísticas Principais Redesenhados -->
        <MainStats
            :stats-data="statsData"
            :loading-stats="loadingStats"
        />

        <!-- Cards de Estatísticas de HTTP Status -->
        <!--        <HttpStats-->
        <!--            :stats-data="statsData"-->
        <!--            :loading-stats="loadingStats"-->
        <!--        />-->

        <div class="row">
            <!-- Gráfico de Tendência dos Últimos 7 Dias -->
            <TrendChart
                :stats-data="statsData"
                :loading-stats="loadingStats"
            />

            <!-- Estatísticas Detalhadas por Método e Tipo de Erro -->
            <div class="col-lg-8">
                <DetailedStats
                    :stats-data="statsData"
                    :loading-stats="loadingStats"
                />
            </div>
        </div>

        <IntegrationsList ref="integrationListRef"/>


    </div>
</template>

<script>
import {mapGetters} from 'vuex'
import BaseCrud from "../Base/BaseCrud"
import ModalRequest from "./ModalRequest.vue"
import SectionContainer from "../Modals/Checklist/SectionContainer.vue"
import http from "../../http"
import MainStats from "./MainStats.vue"
import TrendChart from "./TrendChart.vue"
import DetailedStats from "./DetailedStats.vue"
import IntegrationsList from "./IntegrationsList.vue";

export default {

    components: {
        DetailedStats,
        TrendChart,
        MainStats,
        BaseCrud,
        ModalRequest,
        SectionContainer,
        IntegrationsList
    },

    data() {
        return {
            showFilters: false,
            loadingStats: true,
            filtersLoading: false,
            load: false,
            refreshing: false,
            modalVisualizarLog: false,
            retryLoading: false,
            exportLoading: false,
            loadingReservation: false,
            integrationListRef: null,

            activeTab: 'log',

            statsData: {},
            selectedLog: null,
            reservationDetails: null,

            copiedStates: {
                request: false,
                response: false
            },

            customFilters: {
                status: '',
                method: '',
                error_type: '',
                entity_id: '',
                date_from: '',
                date_to: '',
                http_status: '',
                quick_period: ''
            },

            extra: [
                {
                    id: 1,
                    label: "",
                    class: "fas fa-eye btn btn-crud view",
                    title: "Ver detalhes do log",
                    name: "viewReservation",
                    icon: "btn btn-crud view",
                }
            ],

            table: "integrationLogsTable",
            columns: [
                "id",
                "entity_id",
                "method_name",
                "status",
                "error_type",
                "http_status_code",
                "execution_time",
                "created_at",
                "actions"
            ],
            endPoint: "integration-logs/",
            dataForm: {},

            filterTimeout: null,
        }
    },

    computed: {
        ...mapGetters(['api']),

        url() {
            return this.$store.getters.api + this.endPoint
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.loadStats();

            setTimeout(() => {
                this.load = true;
            }, 100);
        });
    },

    methods: {
        /**
         * Métodos para filtros ativos / Methods for active filters
         */
        hasActiveFilters() {
            return Object.values(this.customFilters).some(filter => filter !== '' && filter !== null)
        },

        getActiveFiltersCount() {
            return Object.values(this.customFilters).filter(filter => filter !== '' && filter !== null).length
        },

        /**
         * Alterna a exibição dos filtros / Toggle filters display
         */
        toggleFilters() {
            this.showFilters = !this.showFilters
        },

        /**
         * Aplica os filtros customizados / Apply custom filters
         */
        async applyFilters() {
            this.filtersLoading = true;
            this.refreshing = true;

            try {
                await this.loadStats();

                await new Promise(resolve => setTimeout(resolve, 300));

                if (this.$refs.integrationListRef) {
                    await this.$refs.integrationListRef.applyFilters(this.customFilters);
                }

            } catch (error) {
                console.error('Erro ao aplicar filtros:', error);
            } finally {
                setTimeout(() => {
                    this.filtersLoading = false;
                    this.refreshing = false;
                }, 500);
            }
        },

        /**
         * Debounce para filtro de texto / Debounce for text filter
         */
        debounceFilter() {
            clearTimeout(this.filterTimeout)
            this.filterTimeout = setTimeout(() => {
                this.applyFilters()
            }, 1000)
        },

        /**
         * Aplica período rápido / Apply quick period
         */
        async applyQuickPeriod() {
            const period = this.customFilters.quick_period
            const now = new Date()

            switch (period) {
                case 'today':
                    this.customFilters.date_from = this.formatDate(now)
                    this.customFilters.date_to = this.formatDate(now)
                    break
                case 'yesterday':
                    const yesterday = new Date(now)
                    yesterday.setDate(yesterday.getDate() - 1)
                    this.customFilters.date_from = this.formatDate(yesterday)
                    this.customFilters.date_to = this.formatDate(yesterday)
                    break
                case 'week':
                    const startOfWeek = new Date(now)
                    startOfWeek.setDate(now.getDate() - now.getDay())
                    this.customFilters.date_from = this.formatDate(startOfWeek)
                    this.customFilters.date_to = this.formatDate(now)
                    break
                case 'month':
                    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
                    this.customFilters.date_from = this.formatDate(startOfMonth)
                    this.customFilters.date_to = this.formatDate(now)
                    break
                case 'last_week':
                    const lastWeekEnd = new Date(now)
                    lastWeekEnd.setDate(now.getDate() - now.getDay() - 1)
                    const lastWeekStart = new Date(lastWeekEnd)
                    lastWeekStart.setDate(lastWeekEnd.getDate() - 6)
                    this.customFilters.date_from = this.formatDate(lastWeekStart)
                    this.customFilters.date_to = this.formatDate(lastWeekEnd)
                    break
                case 'last_month':
                    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0)
                    this.customFilters.date_from = this.formatDate(lastMonth)
                    this.customFilters.date_to = this.formatDate(lastMonthEnd)
                    break
                default:
                    break
            }

            if (period) {
                await this.applyFilters()
            }
        },

        /**
         * Limpa todos os filtros / Clear all filters
         */
        async clearFilters() {
            Object.assign(this.customFilters, {
                status: '',
                method: '',
                error_type: '',
                entity_id: '',
                date_from: '',
                date_to: '',
                http_status: '',
                quick_period: ''
            })

            await this.applyFilters()
        },

        /**
         * Constrói query string para filtros customizados / Build query string for custom filters
         */
        buildCustomFiltersQuery() {
            let query = ''

            Object.keys(this.customFilters).forEach(key => {
                if (this.customFilters[key] && key !== 'quick_period') {
                    query += `&${key}=${encodeURIComponent(this.customFilters[key])}`
                }
            })

            return query
        },

        /**
         * Carrega as estatísticas dos logs usando a nova API / Load log statistics using new API
         */
        async loadStats() {
            this.loadingStats = true
            try {
                const query = this.buildCustomFiltersQuery()
                const url = `${this.api}integration-logs/stats${query ? '?' + query.substring(1) : ''}`

                const response = await http.get(url)

                if (response && response.data) {
                    if (response.data.data) {
                        this.statsData = response.data.data
                    } else {
                        this.statsData = response.data
                    }
                } else {
                    throw new Error('Resposta vazia ou inválida')
                }

            } catch (error) {
                console.error('Erro ao carregar estatísticas:', error)
                this.statsData = {
                    today: {total: 0, success_rate: 0, errors: 0, avg_execution_time: 0},
                    week: {avg_execution_time: 0, total: 0, success_rate: 0},
                    month: {total: 0, success_rate: 0},
                    method_statistics: [],
                    error_statistics: [],
                    recent_failures: [],
                    performance: {slow_requests_week: 0, fast_requests_week: 0},
                    http_status_statistics: [],
                    trend_data: []
                }

                if (this.$store && this.$store.dispatch) {
                    this.$store.dispatch("error", error);
                }
            } finally {
                this.loadingStats = false
            }
        },

        /**
         * Formata data para input / Format date for input
         */
        formatDate(date) {
            return date.toISOString().split('T')[0]
        },

        /**
         * Formata data e hora para exibição / Format date and time for display
         */
        formatDateTime(dateTime) {
            if (!dateTime) return '-'
            return new Date(dateTime).toLocaleString('pt-BR')
        },
    }
}
</script>



// WEBPACK FOOTER //
// src/components/IntegrationLog/Index.vue